import { apiClient } from './../client';
import { API_ENDPOINTS } from './../constant/endpoints';
import type { IApiResponse } from './../types';
import type {
    ICreateFieldInput,
    ICreateModuleInput,
    ICreateSectionInput,
    IModule,
    IModuleField,
    IModuleLayout,
    IModuleLayoutEntity,
    IModuleSection,
    IMoveFieldInput,
    IRecordValueInput,
    IUpdateFieldInput,
    IUpdateModuleInput,
    IUpdateModuleLayoutInput,
    IUpdateSectionInput,
} from '@/features/module-builder/types';

const MB = API_ENDPOINTS.MODULE_BUILDER;

/** GET /contacts/:id/custom-fields response. */
export interface IContactCustomFieldValues {
    /** `{ [fieldId]: value }` — legacy shape. */
    values: Record<string, unknown>;
    /** `{ [fieldName]: value }` — canonical shape (matches record snapshots). */
    valuesByName: Record<string, unknown>;
    fields: IModuleField[];
}

export const moduleBuilderApi = {
    // ── Modules ──────────────────────────────────────────────────────────────
    listModules: (params?: { includeInactive?: boolean }) =>
        apiClient.get<IApiResponse<IModule[]>>(MB.MODULES, { params }).then((r) => r.data.data),

    getModule: (moduleId: string) =>
        apiClient
            .get<IApiResponse<IModuleLayout>>(MB.MODULE_BY_ID(moduleId))
            .then((r) => r.data.data),

    createModule: (input: ICreateModuleInput) =>
        apiClient.post<IApiResponse<{ id: string }>>(MB.MODULES, input).then((r) => r.data.data),

    updateModule: (moduleId: string, input: IUpdateModuleInput) =>
        apiClient
            .patch<IApiResponse<IModule>>(MB.MODULE_BY_ID(moduleId), input)
            .then((r) => r.data.data),

    deleteModule: (moduleId: string) => apiClient.delete(MB.MODULE_BY_ID(moduleId)),

    reorderModules: (orderedIds: string[]) => apiClient.post(MB.MODULES_REORDER, { orderedIds }),

    // ── Sections ─────────────────────────────────────────────────────────────
    createSection: (moduleId: string, input: ICreateSectionInput) =>
        apiClient
            .post<IApiResponse<IModuleSection>>(MB.SECTIONS(moduleId), input)
            .then((r) => r.data.data),

    updateSection: (moduleId: string, sectionId: string, input: IUpdateSectionInput) =>
        apiClient
            .patch<IApiResponse<IModuleSection>>(MB.SECTION_BY_ID(moduleId, sectionId), input)
            .then((r) => r.data.data),

    deleteSection: (moduleId: string, sectionId: string) =>
        apiClient.delete(MB.SECTION_BY_ID(moduleId, sectionId)),

    reorderSections: (moduleId: string, orderedIds: string[]) =>
        apiClient.post(MB.SECTIONS_REORDER(moduleId), { orderedIds }),

    // ── Fields ───────────────────────────────────────────────────────────────
    createField: (moduleId: string, input: ICreateFieldInput) =>
        apiClient
            .post<IApiResponse<IModuleField>>(MB.FIELDS(moduleId), input)
            .then((r) => r.data.data),

    updateField: (moduleId: string, fieldId: string, input: IUpdateFieldInput) =>
        apiClient
            .patch<IApiResponse<IModuleField>>(MB.FIELD_BY_ID(moduleId, fieldId), input)
            .then((r) => r.data.data),

    deleteField: (moduleId: string, fieldId: string) =>
        apiClient.delete(MB.FIELD_BY_ID(moduleId, fieldId)),

    reorderFields: (moduleId: string, sectionId: string, orderedIds: string[]) =>
        apiClient.post(MB.FIELDS_REORDER(moduleId), { sectionId, orderedIds }),

    moveField: (moduleId: string, fieldId: string, input: IMoveFieldInput) =>
        apiClient
            .post<IApiResponse<IModuleField>>(MB.FIELD_MOVE(moduleId, fieldId), input)
            .then((r) => r.data.data),

    // ── Layouts ──────────────────────────────────────────────────────────────
    listLayouts: (moduleId: string) =>
        apiClient
            .get<IApiResponse<IModuleLayoutEntity[]>>(MB.LAYOUTS(moduleId))
            .then((r) => r.data.data),

    updateLayout: (moduleId: string, layoutId: string, input: IUpdateModuleLayoutInput) =>
        apiClient
            .patch<IApiResponse<IModuleLayoutEntity>>(MB.LAYOUT_BY_ID(moduleId, layoutId), input)
            .then((r) => r.data.data),

    // ── Contact custom fields (bridge) ────────────────────────────────────────
    getContactCustomFields: (contactId: string) =>
        apiClient
            .get<IApiResponse<IContactCustomFieldValues>>(MB.CONTACT_CUSTOM_FIELDS(contactId))
            .then((r) => r.data.data),

    setContactCustomFields: (contactId: string, values: IRecordValueInput[]) =>
        apiClient.put(MB.CONTACT_CUSTOM_FIELDS(contactId), { values }),
};
